import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "../lang/LanguageContext";

const inputCls =
    "w-full bg-white border-2 border-soil/15 rounded-2xl px-4 py-3 text-soil placeholder:text-soil/35 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/25 transition-all";
const labelCls = "block font-bold text-sm text-soil mb-1.5";

const INITIAL = {
    parent_name: "",
    phone: "",
    email: "",
    child_name: "",
    child_age: "",
    days_hours: "",
    start_date: "",
    tour_time: "",
    heard_about: "",
    preferred_language: "English",
    message: "",
};

export default function TourForm() {
    const [values, setValues] = useState(INITIAL);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const { lang, t } = useLang();

    const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

    const validate = (v) => {
        const errs = {};
        if (!v.parent_name.trim()) errs.parent_name = t("Please tell us your name.");
        if (!v.phone.trim()) errs.phone = t("A phone number helps Asia reply fastest.");
        if (v.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) errs.email = t("That email looks incomplete.");
        if (!v.child_age.trim()) errs.child_age = t("Tell us your child's age or due date.");
        if (!v.days_hours.trim()) errs.days_hours = t("Which days and hours do you need?");
        return errs;
    };

    const submit = async (e) => {
        e.preventDefault();
        const errs = validate(values);
        setErrors(errs);
        if (Object.keys(errs).length) return;
        setSending(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/tour-requests`, values, { timeout: 30000 });
            setSent(true);
        } catch (err) {
            toast.error(t("Hmm, that didn't send."), {
                description: t("Please try again — or call or text Asia at 516-324-3418."),
            });
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <div
                className="bg-white border-2 border-sage/30 rounded-[2rem] p-8 md:p-10 text-center"
                data-testid="tour-form-success-message"
            >
                <CheckCircle2 size={56} className="text-sage mx-auto mb-4" />
                <h3 className="font-display font-semibold text-2xl md:text-3xl mb-3">{t("Thank you!")}</h3>
                <p className="text-soilmuted text-lg leading-relaxed max-w-md mx-auto">
                    {t("Asia will reach out within 24 hours to set up your visit.")}
                </p>
                <p className="mt-4 text-sm text-soil/50">{t("In a hurry? Call or text 516-324-3418 any time.")}</p>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            noValidate
            className="bg-white border-2 border-soil/10 rounded-[2rem] p-6 md:p-9 shadow-sm"
            data-testid="tour-form"
        >
            <div className="grid sm:grid-cols-2 gap-5">
                <div>
                    <label className={labelCls} htmlFor="tf-parent">{t("Parent name *")}</label>
                    <input id="tf-parent" className={inputCls} data-testid="tour-form-parent-name" value={values.parent_name} onChange={set("parent_name")} placeholder={t("Your name")} />
                    {errors.parent_name && <p className="text-coral text-xs font-bold mt-1.5" data-testid="tour-form-error-parent-name">{errors.parent_name}</p>}
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-phone">{t("Phone *")}</label>
                    <input id="tf-phone" type="tel" className={inputCls} data-testid="tour-form-phone" value={values.phone} onChange={set("phone")} placeholder="(516) 555-0123" />
                    {errors.phone && <p className="text-coral text-xs font-bold mt-1.5" data-testid="tour-form-error-phone">{errors.phone}</p>}
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-email">{t("Email")}</label>
                    <input id="tf-email" type="email" className={inputCls} data-testid="tour-form-email" value={values.email} onChange={set("email")} placeholder="you@email.com" />
                    {errors.email && <p className="text-coral text-xs font-bold mt-1.5">{errors.email}</p>}
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-child-name">{t("Child's first name")}</label>
                    <input id="tf-child-name" className={inputCls} data-testid="tour-form-child-name" value={values.child_name} onChange={set("child_name")} placeholder={t("First name only")} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-child-age">{t("Child's age or due date *")}</label>
                    <input id="tf-child-age" className={inputCls} data-testid="tour-form-child-age" value={values.child_age} onChange={set("child_age")} placeholder={t("e.g. 18 months")} />
                    {errors.child_age && <p className="text-coral text-xs font-bold mt-1.5">{errors.child_age}</p>}
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-days">{t("Days & hours needed *")}</label>
                    <input id="tf-days" className={inputCls} data-testid="tour-form-days-hours" value={values.days_hours} onChange={set("days_hours")} placeholder={t("e.g. Mon–Fri, 8:30–5:00")} />
                    {errors.days_hours && <p className="text-coral text-xs font-bold mt-1.5">{errors.days_hours}</p>}
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-start">{t("Desired start date")}</label>
                    <input id="tf-start" type="date" className={`${inputCls} min-w-0`} data-testid="tour-form-start-date" value={values.start_date} onChange={set("start_date")} />
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-time">{t("Best time for a tour")}</label>
                    <select id="tf-time" className={inputCls} data-testid="tour-form-tour-time" value={values.tour_time} onChange={set("tour_time")}>
                        <option value="">{t("Choose one…")}</option>
                        <option value="Morning">{t("Morning")}</option>
                        <option value="Afternoon">{t("Afternoon")}</option>
                        <option value="Evening">{t("Evening")}</option>
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-lang">{t("Preferred language")}</label>
                    <select id="tf-lang" className={inputCls} data-testid="tour-form-preferred-language" value={values.preferred_language} onChange={set("preferred_language")}>
                        <option value="English">English</option>
                        <option value="Español">Español</option>
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor="tf-heard">{t("How did you hear about us?")}</label>
                    <select id="tf-heard" className={inputCls} data-testid="tour-form-heard-about" value={values.heard_about} onChange={set("heard_about")}>
                        <option value="">{t("Choose one…")}</option>
                        <option value="Google">Google</option>
                        <option value="Facebook">Facebook</option>
                        <option value="TikTok">TikTok</option>
                        <option value="Friend">{t("Friend")}</option>
                        <option value="Other">{t("Other")}</option>
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="tf-message">{t("Message")}</label>
                    <textarea id="tf-message" rows={4} className={inputCls} data-testid="tour-form-message" value={values.message} onChange={set("message")} placeholder={t("Anything you'd like Asia to know?")} />
                </div>
            </div>

            <button
                type="submit"
                disabled={sending}
                data-testid="tour-form-submit-button"
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-sun hover:bg-suncdeep disabled:opacity-60 text-soil font-extrabold text-lg px-9 py-4 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
                {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                {sending ? t("Sending…") : t("Request a Tour")}
            </button>
        </form>
    );
}
